<template>
    <!-- Main container for centering the form -->
    <div class="flex flex-col items-center justify-center">
        <UAuthForm
            :schema="schema"
            :fields="fields"
            title="Forgot Password"
            description="An email will be sent to you"
            :loading="isSubmitting"
            :disabled="isSubmitting"
            :ui="{
                form: 'space-y-5 ', // Increases spacing between fields
                header: 'mb-6 mt-6 ',
                leading: 'mb-0 pb-2',
                footer: 'mt-5',
                description: 'text-muted font-normal not-italic',
                leadingIcon: 'size-6.5 text-(--ui-text-primary-highlighted)',
                title: 'text-(--ui-text-primary-highlighted)',
            }"
            @submit="sendOtp"
        >
            <template #submit>
                <div class="space-y-4">
                    <!-- Submit Button -->
                    <UButton
                        type="submit"
                        block
                        :loading="isSubmitting"
                        :disabled="isSubmitting"
                        class="cursor-pointer h-15 bg-(--color-action-800) hover:bg-(--color-action-900) active:bg-(--color-action-700) disabled:bg-(--color-neutral-800) rounded font-normal"
                    >
                        Submit
                    </UButton>
                </div>
            </template>
            <!-- Footer slot for additional actions -->
            <template #footer>
                Know your password ?
                <SharedLinkButton
                    :disabled="isSubmitting"
                    class="underline decoration-solid decoration-auto decoration-skip-ink-auto underline-offset-auto underline-from-font text-var(--ui-text-primary-subdued) cursor-pointer font-medium"
                    @click.prevent="openLogin"
                >
                    Sign In
                </SharedLinkButton>
            </template>
        </UAuthForm>
    </div>
</template>

<script setup lang="ts">
// Imports
import * as z from 'zod' // Zod for schema validation
import type { ForgotPassword, AuthFormField } from '~~/layers/base/types/auth' // Types for form
import { authClient } from '~~/layers/base/lib/auth-client'

// Emits for communicating with parent components
const emit = defineEmits(['open-login', 'send-otp-success', 'send-otp-failed'])

// Props definition with default values
interface Props {
    inProgress?: boolean // Loading state for form
}
withDefaults(defineProps<Props>(), {
    inProgress: false, // Default loading state
})

// Local loading state for the Better Auth request (parent doesn't drive `inProgress` today).
const isSubmitting = ref(false)

// Form fields definition
const fields: AuthFormField[] = [
    {
        name: 'email',
        label: 'Email',
        type: 'text',
        placeholder: 'Enter your email',
        ui: {
            base: 'placeholder:opacity-100 h-15 placeholder:italic placeholder:text-(--ui-text-primary-dimmed) text-(--ui-text-primary) ring-2 ring-(--ui-border-primary-elevated)',
        }, // Email field
    },
]

// Zod schema for form validation
const schema = z.object({
    email: z
        .string({ message: 'Email is required' })
        .min(1, 'Email is required.')
        .email('Invalid email'), // Email validation
})

// Request the forgot-password OTP via Better Auth
const sendOtp = async (payload: { data: ForgotPassword }): Promise<void> => {
    isSubmitting.value = true
    try {
        const { error } = await authClient.emailOtp.sendVerificationOtp({
            email: payload.data.email,
            type: 'forget-password',
        })
        if (error) {
            throw new Error(error.message ?? 'Failed to send reset code')
        }
        emit('send-otp-success', payload.data) // Emit success event
    } catch (errorObject: unknown) {
        emit('send-otp-failed', errorObject) // Emit failure event
    } finally {
        isSubmitting.value = false
    }
}

// Open sign in dialog handler
const openLogin = (): void => {
    emit('open-login', 'login') // Emit open sign in event
}
</script>

<style scoped>
:deep(label) {
    color: var(--ui-text-primary) !important;
    font-size: 0.875rem !important;
    font-style: normal !important;
    margin-bottom: 4px;
}
</style>
